import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, FC } from "react";
import { CLIPS } from "./content";
import type { Clip } from "./content";
import { Viewer } from "./Gallery";
import { pad, reducedMotion, useInView, useNarrow } from "./hooks";

const SHOWN = CLIPS.map(clip => ({ title: clip.label, video: clip.video }));
/** A row and how much of its width the films in it take; the rest, on a last short row, stays empty. */
interface Row {
  clips: Clip[];
  spare: number;
}

/**
 * Breaks the films into rows. In a row every film has the same height, so its
 * width is its ratio's share; a row closes when the ratios add up to `full`.
 */
function rows(clips: Clip[], full: number): Row[] {
  const out: Row[] = [];
  let row: Clip[] = [];
  let sum = 0;
  const close = () => {
    if (row.length) out.push({ clips: row, spare: Math.max(0, full - sum) });
    row = [];
    sum = 0;
  };
  for (const clip of clips) {
    // a wide film after a narrow start would shrink the whole row
    if (sum >= full * 0.5 && sum + clip.ratio > full * 1.5) {
      // wide enough to be a row by itself: it goes first, and the open row waits for a film that fits
      if (clip.ratio >= full * 0.98) {
        out.push({ clips: [clip], spare: 0 });
        continue;
      }
      close();
    }
    row.push(clip);
    sum += clip.ratio;
    if (sum >= full * 0.98) close();
  }
  close();
  // only the last row may fall short; any other closed early and should fill its width
  return out.map((item, n) => (n < out.length - 1 ? { ...item, spare: 0 } : item));
}

interface MovingProps {
  src: string;
}

/** A muted loop that plays only while it is on screen. */
export const Moving: FC<MovingProps> = ({ src }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const seen = useInView(ref, "0px");

  useEffect(() => {
    const video = ref.current;
    if (!video || reducedMotion()) return;
    if (seen) void video.play().catch(() => undefined);
    else video.pause();
  }, [seen]);

  return <video ref={ref} className="sd-cover" src={src} muted loop playsInline preload="metadata" />;
};

/**
 * The gallery: studio work in rows, every film whole at its own ratio. A film opens at full size, clean.
 */
export const Passes: FC = () => {
  const [at, setAt] = useState<number | null>(null);
  const narrow = useNarrow();
  const layout = useMemo(() => rows(CLIPS, narrow ? 1.1 : 2.95), [narrow]);
  const count = CLIPS.length;
  const step = useCallback((by: number) => setAt(n => (n === null ? n : (n + by + count) % count)), [count]);
  const close = useCallback(() => setAt(null), []);

  return (
    <section id="gallery" className="sd-passes">
      <header className="sd-cases-head">
        <p className="sd-label">selected work</p>
        <h2>
          generative ai
          <br />
          since 2016
        </h2>
        <p className="sd-label">{pad(count)} / works</p>
      </header>
      <div className="sd-reel">
        {layout.map(row => (
          <div key={row.clips[0].video} className="sd-reel-row">
            {row.clips.map(clip => (
              <figure key={clip.video} className="sd-cell" style={{ "--ratio": clip.ratio } as CSSProperties}>
                <button type="button" aria-label={`open ${clip.label}`} onClick={() => setAt(CLIPS.indexOf(clip))}>
                  <Moving src={clip.video} />
                </button>
                <figcaption>
                  <span>{clip.label}</span>
                </figcaption>
              </figure>
            ))}
            {row.spare > 0.05 && <i style={{ "--ratio": row.spare } as CSSProperties} />}
          </div>
        ))}
      </div>
      {at !== null && <Viewer pieces={SHOWN} at={at} onStep={step} onClose={close} />}
    </section>
  );
};
